import React, { useState, useCallback, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { format } from "date-fns";
import { MdOutlineChat, MdDelete, MdLogin } from "react-icons/md";
import { apiEndpoints, authToken } from "../../config/config";
import { toast } from "react-toastify";
import { useChatbotConfig } from "../../contexts/ChatbotConfigContext";
import axios from "axios";
import { v4 as uuidv4 } from "uuid";
import Participants from "./Participants";
import image from "../../assets/user.png";
import { clearLoginTimestamp } from "../../utils/sessionUtils";
import { useStaticTranslations } from "../../utils/staticTranslations"; 
import { useLanguage } from "../../contexts/LanguageContext";
import { useTranslation } from '../../hooks/useTranslation';

const ChatHistory = ({
  onStartNewChat,
  onSelectChat,
  onSelectExistingChat,
  onSelectReminderChat,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { config } = useChatbotConfig();
  const [isLogin, setIsLogin] = useState(
    localStorage.getItem("isLoggedIn") === "true"
  );
  const isExpandedWindow = window.opener != null;
  // Get chat history from localStorage or state management
  const [chatHistory, setChatHistory] = useState(() => {
    try {
      const savedHistory = localStorage.getItem("chatHistory");
      return savedHistory ? JSON.parse(savedHistory) : [];
    } catch (error) {
      console.error("Error loading chat history:", error);
      return [];
    }
  });
  const [conversations, setConversations] = useState([]);
  const [aiChatData, setAiChatData] = useState({
    id: null,
    chatMessages: [],
  });
  const [showParticipants, setShowParticipants] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [chatToDelete, setChatToDelete] = useState(null);
  const [logoUrl, setLogoUrl] = useState(image);
  const { currentLanguage } = useLanguage(); 
  const { commonMessage, buttonLabel } = useStaticTranslations(currentLanguage.code); 
  const [reminderData, setReminderData] = useState(() => {
    try {
      const savedReminders = localStorage.getItem("chatbot_reminders");
      return savedReminders ? JSON.parse(savedReminders) : [];
    } catch (error) {
      console.error("Error loading reminders:", error);
      return [];
    }
  });
  const [showReminderForm, setShowReminderForm] = useState(false);

  // Extract accessToken from URL
  const queryParams = new URLSearchParams(location.search);
  const accessTokenFromUrl = queryParams.get("accessToken");

  const handleLogout = useCallback(async () => {
    try {
      const authToken = localStorage.getItem("authToken");
      const accessToken = localStorage.getItem("accessToken");

      try {
        await axios.post(apiEndpoints.logout, null, {
          headers: {
            clientToken: `Bearer ${accessToken}`,
            Authorization: `Bearer ${authToken}`,
            Accept: "application/json",
          },
        });
      } catch (logoutError) {
        console.error("Logout API error:", logoutError);
      }

      // Clear login timestamp
      clearLoginTimestamp();

      // Clear local storage
      localStorage.removeItem("isLoggedIn");
      localStorage.removeItem("authToken");
      localStorage.removeItem("accessToken");
      
      // Update state
      setIsLogin(false);
      
      // Reload the page
      window.location.reload();
    } catch (error) {
      console.error("Logout error:", error);
    }
  }, []);

  const fetchConversations = async () => {
    try {
      if (!isLogin) {
        return;
      }

      if (!accessTokenFromUrl) {
        if (!toast.isActive("no-access-token")) {
          toast.error(
            "No access token provided, Please click on expand button and then come back!!"
          );
        }
        return;
      }

      const response = await axios.get(apiEndpoints.conversations, {
        headers: {
          clientToken: `Bearer ${accessTokenFromUrl}`,
          Accept: "application/json",
          Authorization: `Bearer ${authToken}`,
        },
      });
      setConversations(response.data);
      console.log(
        "Conversations:",
        response.data.map((chat) => chat.conversation_id)
      );
    } catch (error) {
      console.error("Error fetching conversations:", error);
      if (error.response?.status === 401) {
        toast.error("Unauthorized access, Please login again!!");
        handleLogout();
      }
    }
  };

  const handleSearchParticipants = () => {
    if (!isLogin) {
      // Navigate to Chat.js page within the chatbot
      onStartNewChat();
    } else {
      // If already logged in, show the Participants page
      setShowParticipants(true);
    }
  };

  const handleSelectParticipant = (participant) => {
    // Handle participant selection
    console.log("Selected participant:", participant);
    // You can implement your logic here to start a chat with the selected participant
    setShowParticipants(false);
    onStartNewChat();
  };

  const handleBackFromParticipants = () => {
    setShowParticipants(false);
  };

  useEffect(() => {
    // if (accessTokenFromUrl) {
    fetchConversations();
    // }
  }, [accessTokenFromUrl]);

  useEffect(() => {
    // Check if AI chat data exists in localStorage
    const savedAiChatData = localStorage.getItem("aiChatMessages");
    let aiChatObj;

    // Get logo_url from chatBotData
    const storedData = localStorage.getItem("chatBotData");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        if (parsedData.logo_url) {
          setLogoUrl(parsedData.logo_url);
        } else {
          setLogoUrl(image);
        }
      } catch (error) {
        setLogoUrl(image);
      }
    } else {
      setLogoUrl(image);
    }

    if (savedAiChatData) {
      try {
        // Try to parse existing data
        aiChatObj = JSON.parse(savedAiChatData);

        // If it exists but doesn't have the expected structure, create it
        if (!aiChatObj.id || !Array.isArray(aiChatObj.chatMessages)) {
          createNewAiChat();
        } else {
          setAiChatData(aiChatObj);
        }
      } catch (error) {
        console.error("Error parsing AI chat data:", error);
        createNewAiChat();
      }
    } else {
      // If no data exists, create a new AI chat
      createNewAiChat();
    }
  }, []);

  const createNewAiChat = () => {
    // Always fetch the latest greetingMessage from chatBotData
    let greetingMessage = null;
    const storedData = localStorage.getItem("chatBotData");
    if (storedData) {
      try {
        const parsedData = JSON.parse(storedData);
        greetingMessage = parsedData.greetingMessage;
      } catch (error) {
        console.error("Error parsing chatBotData:", error);
      }
    }
    const newChatId = uuidv4();
    const newAiChatObj = {
      id: newChatId,
      chatMessages: [
        {
          type: "bot",
          content: greetingMessage || "How can I help you?",
          timestamp: new Date(),
        },
      ],
    };
    localStorage.setItem("aiChatMessages", JSON.stringify(newAiChatObj));
    setAiChatData(newAiChatObj);
    return newAiChatObj;
  };

  const handleAiChatSelect = () => {
    // Get the latest AI chat data
    const savedAiChatData = localStorage.getItem("aiChatMessages");
    let aiChatObj;

    if (savedAiChatData) {
      try {
        aiChatObj = JSON.parse(savedAiChatData);
      } catch (error) {
        aiChatObj = createNewAiChat();
      }
    } else {
      aiChatObj = createNewAiChat();
    }

    onSelectChat(aiChatObj, true);
  };

  const handleChatSelect = useCallback(
    (chat) => {
      console.log("handleChatSelect called with chat:", chat);
      if (chat.conversation_id) {
        // Call the passed function with the conversation ID
        onSelectExistingChat(chat.conversation_id);
      }
    },
    [onSelectExistingChat]
  );

  // Animation variants
  const containerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    initial: { y: 20, opacity: 0 },
    animate: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 24,
      },
    },
  };

  const handleDeleteClick = useCallback((chatId) => {
    setChatToDelete(chatId);
    setShowDeleteConfirm(true);
  }, []);

  const handleDeleteConfirm = useCallback(() => {
    try {
      if (chatToDelete === "all") {
        // Clear all chat history
        localStorage.removeItem("chatHistory");
        setChatHistory([]);
        toast.success("Chat history cleared successfully");
      } else {
        // Delete single chat
        const updatedHistory = chatHistory.filter(
          (chat) => chat.id !== chatToDelete
        );
        localStorage.setItem("chatHistory", JSON.stringify(updatedHistory));
        setChatHistory(updatedHistory);
        toast.success("Chat deleted successfully");
      }
    } catch (error) {
      console.error("Error deleting chat:", error);
      toast.error("Failed to delete chat");
    }
    setShowDeleteConfirm(false);
    setChatToDelete(null);
  }, [chatToDelete, chatHistory]);

  const DeleteConfirmDialog = () => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
    >
      <motion.div
        initial={{ scale: 0.9 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.9 }}
        className="bg-white rounded-lg p-6 max-w-sm mx-4"
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          {chatToDelete === "all"
            ? "Clear All Chat History?"
            : "Delete this chat?"}
        </h3>
        <p className="text-gray-600 mb-6">
          {chatToDelete === "all"
            ? "This will permanently delete all your chat history. This action cannot be undone."
            : "This chat will be permanently deleted. This action cannot be undone."}
        </p>
        <div className="flex justify-end space-x-3">
          <button
            onClick={() => setShowDeleteConfirm(false)}
            className="px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleDeleteConfirm}
            className="px-4 py-2 rounded-lg text-white bg-red-500 hover:bg-red-600 transition-colors"
          >
            Delete
          </button>
        </div>
      </motion.div>
    </motion.div>
  );

  const EmptyState = () => (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      className="flex flex-col items-center justify-center h-full space-y-4 py-8"
    >
      <motion.div
        variants={itemVariants}
        className="bg-gray-100 p-4 rounded-full"
      >
        <MdOutlineChat size={48} className="text-gray-400" />
      </motion.div>
      <motion.h3
        variants={itemVariants}
        className="text-gray-700 text-lg font-medium"
      >
        No Chat History
      </motion.h3>
      <motion.p
        variants={itemVariants}
        className="text-gray-500 text-sm text-center max-w-xs"
      >
        Start a new conversation to begin chatting with our assistant
      </motion.p>
    </motion.div>
  );

  const ChatList = () => (
    <motion.div
      variants={containerVariants}
      initial="initial"
      animate="animate"
      className="space-y-3"
    >
      {chatHistory.map((chat) => (
        <motion.div
          key={chat.id}
          variants={itemVariants}
          className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 relative group"
        >
          <div className="cursor-pointer" onClick={() => onSelectChat(chat)}>
            <div className="flex justify-between items-center mb-2">
              <div className="flex items-center space-x-2">
                <span className="text-sm font-medium text-gray-800">
                  {chat.title ||
                    `Conversation ${format(
                      new Date(chat.timestamp),
                      "MMM d, yyyy"
                    )}`}
                </span>
                {chat.isNew && (
                  <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                    New
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-gray-600 line-clamp-2">
              {chat.lastMessage || "Start of conversation"}
            </p>
            <div className="flex justify-between items-center mt-2">
              <span className="text-xs text-gray-500">
                {chat.messageCount || 0} messages
              </span>

              <span className="text-xs text-gray-500">
                {format(new Date(chat.timestamp), "h:mm a")}
              </span>
            </div>
          </div>

          {/* Delete button */}
          <button
            onClick={() => handleDeleteClick(chat.id)}
            className="absolute right-2 top-2 p-2 rounded-full text-gray-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
            title="Delete chat"
          >
            <MdDelete size={20} />
          </button>
        </motion.div>
      ))}
    </motion.div>
  );

  const getLastAiChatMessage = () => {
    const savedAiChatData = localStorage.getItem("aiChatMessages");
    let aiChatObj;

    if (savedAiChatData) {
      try {
        aiChatObj = JSON.parse(savedAiChatData);
      } catch (error) {
        console.error("Error parsing AI chat data:", error);
        return getGreetingMessage();
      }

      // Check if chatMessages array exists and is not empty
      if (aiChatObj.chatMessages && aiChatObj.chatMessages.length > 0) {
        // Get the last message
        const lastMessage =
          aiChatObj.chatMessages[aiChatObj.chatMessages.length - 1];

        // Check if the last message is defined and has content
        if (lastMessage && lastMessage.content) {
          return lastMessage.content;
        }
      }
    }

    // Return greeting message if no chat messages are present
    return getGreetingMessage();
  };

  const getGreetingMessage = () => {
    const chatBotData = localStorage.getItem("chatBotData");
    if (chatBotData) {
      try {
        const parsedData = JSON.parse(chatBotData);
        return (
          parsedData.greetingMessage || "Welcome! How can I assist you today?"
        );
      } catch (error) {
        console.error("Error parsing chatBotData:", error);
      }
    }
    return "Welcome! How can I assist you today?";
  };

  const lastMessage = getLastAiChatMessage();

  const handleReminderSelect = () => {
    const savedReminders = localStorage.getItem("chatbot_reminders");
    let reminderMessages = [];
  
    if (savedReminders) {
      try {
        const reminders = JSON.parse(savedReminders);
        
        if (Array.isArray(reminders)) {
          // Sort reminders by creation date (newest first)
          const sortedReminders = reminders.sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          );
          
          reminderMessages = sortedReminders.map((reminder) => {
            const meetingDate = new Date(reminder.meetingDate);
            const now = new Date();
            const isPastMeeting = meetingDate < now;
            let messageContent = "";
            let messageTimestamp = new Date(reminder.createdAt);
            if (isPastMeeting) {
              // Meeting completed message
              const formattedDate = meetingDate.toLocaleDateString('en-IN');
              const formattedTime = meetingDate.toLocaleTimeString('en-IN', { 
                hour: '2-digit', 
                minute: '2-digit',
                hour12: true 
              });
              messageContent = `✅ Meeting completed with ${reminder.clientName} on ${formattedDate} at ${formattedTime}`;
              messageTimestamp = meetingDate;
            } else if (Array.isArray(reminder.notificationMessages) && reminder.notificationMessages.length > 0) {
              // Latest notification message
              const latestNotification = reminder.notificationMessages[reminder.notificationMessages.length - 1];
              messageContent = latestNotification.content;
              messageTimestamp = new Date(latestNotification.timestamp);
            } else if (reminder.schedulingMessage) {
              // Scheduling message
              messageContent = reminder.schedulingMessage;
              messageTimestamp = new Date(reminder.createdAt);
            } else {
              // Fallback
              messageContent = `Meeting with ${reminder.clientName} on ${meetingDate.toLocaleString()}`;
              messageTimestamp = meetingDate;
            }
            return {
              type: "bot",
              content: messageContent,
              timestamp: messageTimestamp,
              isReminderMessage: true,
              reminderData: reminder
            };
          });
        }
      } catch (error) {
        console.error("Error parsing reminders:", error);
      }
    }
  
    // Add welcome message if no reminders exist
    if (reminderMessages.length === 0) {
      reminderMessages = [
        {
          type: "bot",
          content: "Welcome to your meeting reminders! 📅\n\nNo reminders found. Type '/reminder' or use the reminder button to schedule your first meeting reminder.\n\nI'll automatically send you notifications 30, 15, and 5 minutes before your meetings!",
          timestamp: new Date(),
          isWelcomeMessage: true
        }
      ];
    } else {
      // Add summary message at the beginning
      const totalReminders = reminderMessages.length;
      const activeReminders = reminderMessages.filter(msg => {
        if (msg.reminderData) {
          const meetingDate = new Date(msg.reminderData.meetingDate);
          return meetingDate > new Date();
        }
        return false;
      }).length;
      
      const summaryMessage = {
        type: "bot",
        content: `📊 **Meeting Reminders Summary**\n\nTotal reminders: ${totalReminders}\nActive (upcoming): ${activeReminders}\nCompleted: ${totalReminders - activeReminders}\n\nType '/reminder' to schedule a new meeting reminder.`,
        timestamp: new Date(),
        isSummaryMessage: true
      };
      
      reminderMessages.unshift(summaryMessage);
    }
  
    onSelectReminderChat({ reminders: reminderMessages });
  };

  useEffect(() => {
    if (showReminderForm) {
      handleReminderSelect();
    }
  }, [showReminderForm]);

  const getLastReminderMessage = () => {
    const savedReminders = localStorage.getItem("chatbot_reminders");
    let reminderObj;
  
    if (savedReminders) {
      try {
        reminderObj = JSON.parse(savedReminders);
        
        // Ensure it's an array
        if (Array.isArray(reminderObj) && reminderObj.length > 0) {
          // Sort by creation date to get the latest
          const sortedReminders = reminderObj.sort((a, b) => 
            new Date(b.createdAt) - new Date(a.createdAt)
          );
          
          const latestReminder = sortedReminders[0];
          const totalReminders = reminderObj.length;
          const currentDate = new Date();
          
          // Count active (future) reminders
          const activeReminders = reminderObj.filter(reminder => {
            const meetingDate = new Date(reminder.meetingDate);
            return meetingDate > currentDate;
          }).length;
          
          if (latestReminder) {
            // Check if the latest reminder is from Azure
            if (latestReminder.source === 'azure') {
              const meetingDate = new Date(latestReminder.meetingDate);
              const now = new Date();
              const isPastMeeting = meetingDate < now;
              
              if (isPastMeeting) {
                return `Meeting completed with ${latestReminder.clientName}. Total reminders: ${totalReminders}`;
              } else {
                const formattedDate = meetingDate.toLocaleDateString('en-IN');
                const formattedTime = meetingDate.toLocaleTimeString('en-IN', { 
                  hour: '2-digit', 
                  minute: '2-digit',
                  hour12: true 
                });
                return `Next meeting: ${latestReminder.clientName} on ${formattedDate} at ${formattedTime}. Active reminders: ${activeReminders}`;
              }
            } else {
              // Legacy reminder format
              if (latestReminder.chatMessage) {
                return latestReminder.chatMessage;
              }
              const meetingDate = new Date(latestReminder.meetingDate);
              const formattedDate = meetingDate.toLocaleDateString();
              const formattedTime = meetingDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
              return `Meeting scheduled with ${latestReminder.clientName} at ${formattedDate} ${formattedTime}`;
            }
          }
        }
      } catch (error) {
        console.error("Error parsing reminder data:", error);
      }
    }
  
    return "Welcome to your meeting reminders! 📅\n\nNo reminders found. Type '/reminder' or use the reminder button to schedule your first meeting reminder.\n\nI'll automatically send you notifications 30, 15, and 5 minutes before your meetings!";
  };

  const lastReminderMessage = getLastReminderMessage();

  const { translated: translatedLastMessage } = useTranslation(lastMessage);
  const { translated: translatedLastReminderMessage } = useTranslation(lastReminderMessage);

  return (
    <div className="flex flex-col h-full bg-gray-50">
      {showParticipants ? (
        <div className="flex flex-col h-full">
          <div className={`p-3 ${
            !isExpandedWindow ? "ml-3" : "ml-10 "
          } border-b flex items-center`}>
            <h2 className="font-bold text-lg">{commonMessage('participants')}</h2>
          </div>
          <Participants
            onSelectParticipant={handleSelectParticipant}
            onSelectExistingChat={onSelectExistingChat}
          />
        </div>
      ) : (
        <>
          <div className="flex-grow overflow-y-auto p-4">
            <motion.div
              key={aiChatData.id}
              variants={itemVariants}
              className="relative mb-4 p-px rounded-lg group"
            >
              <div className="absolute inset-0 rounded-lg bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 opacity-80"></div>

              <div className="relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 w-full h-full">
                <div
                  className="cursor-pointer flex flex-col"
                  onClick={handleAiChatSelect}
                >
                  <div className="flex items-center">
                    <div className="w-[4rem] rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={logoUrl}
                        alt="AI Chat Logo"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = image;
                        }}
                      />
                    </div>
                    <span className="text-lg font-semibold mr-2 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-transparent bg-clip-text">
                      {commonMessage('customerSupport')}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full ml-[0.5rem !important]">
                      AI
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-2">
                    {translatedLastMessage}
                  </p>
                  <div className="flex justify-end items-center mt-2">
                    <span className="text-xs text-gray-500">
                      {aiChatData.timestamp
                        ? format(new Date(aiChatData.timestamp), "h:mm a")
                        : new Date().toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
            <motion.div
              key={reminderData.id}
              variants={itemVariants}
              className="relative mb-4 p-px rounded-lg group"
            >
              <div className="absolute inset-0 rounded-lg"></div>

              <div className="relative bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 w-full h-full">
                <div
                  className="cursor-pointer flex flex-col"
                  onClick={handleReminderSelect}
                >
                  <div className="flex items-center">
                    <div className="w-[4rem] rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={logoUrl}
                        alt="Reminder Logo"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = image;
                        }}
                      />
                    </div>
                    <span className="text-lg font-semibold mr-2">
                      {commonMessage('reminders')}
                    </span>
                    <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full ml-[0.5rem !important]">
                      {commonMessage('reminder')}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-2">
                    {translatedLastReminderMessage}
                  </p>
                  <div className="flex justify-end items-center mt-2">
                    <span className="text-xs text-gray-500">
                      {reminderData.timestamp
                        ? format(new Date(reminderData.timestamp), "h:mm a")
                        : new Date().toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                          })}
                    </span>
                  </div>
                </div>
              </div>
            </motion.div>
            <ChatList />
            {isLogin && (
              <motion.div
                variants={containerVariants}
                initial="initial"
                animate="animate"
                className="space-y-3"
              >
                {conversations.map((chat) => (
                  <motion.div
                    key={chat.id}
                    variants={itemVariants}
                    className="bg-white rounded-lg shadow-sm hover:shadow-md transition-shadow p-4 relative group"
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        console.log("Selected chat with ID:", chat.id);
                        handleChatSelect(chat);
                      }}
                    >
                      <div className="flex justify-between items-center mb-2">
                        <div className="flex items-center space-x-2">
                          <div className="w-12 h-12 rounded-full overflow-hidden bg-gray-200 flex-shrink-0">
                            <img
                              src={image}
                              alt="User"
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <span className="text-sm font-medium text-gray-800">
                            {chat.custom_title}
                          </span>
                          {chat.isNew && (
                            <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                              New
                            </span>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {chat.last_message?.updated_at
                            ? format(
                                new Date(chat.last_message?.updated_at),
                                "HH:mm"
                              )
                            : "N/A"}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            )}
            {/* {chatHistory.length === 0 && <EmptyState />} */}
          </div>

          <div>
            <div
              className={`${
                isExpandedWindow
                  ? "flex justify-center bg-white pt-2 space-x-4"
                  : "flex flex-col space-y-3 px-4 py-4"
              }`}
            >
              {isLogin && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleSearchParticipants}
                  className={`${
                    isExpandedWindow ? "w-64" : "w-full"
                  } p-3 rounded-lg text-white font-medium shadow-sm flex items-center justify-center space-x-2 ${
                    isExpandedWindow ? "text-md" : ""
                  }`}
                  style={{ backgroundColor: config.color }}
                >
                  <MdOutlineChat size={isExpandedWindow ? 20 : 20} />
                  <span>{buttonLabel('startNewConversation')}</span>
                </motion.button>
              )}

              {/* {chatHistory.length > 0 && (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => handleDeleteClick('all')}
                  className={`${
                    isExpandedWindow ? 'w-64' : 'w-full'
                  } p-3 rounded-lg text-red-500 font-medium border border-red-500 hover:bg-red-50 transition-colors flex items-center justify-center space-x-2 ${
                    isExpandedWindow ? 'text-md' : ''
                  }`}
                >
                  <MdDelete size={isExpandedWindow ? 20 : 20} />
                  <span>Clear Chat History</span>
                </motion.button>
              )} */}
            </div>
            {/* {isExpandedWindow && (
              <div className="p-2 bg-white text-gray-400 text-center text-xs rounded-b-lg">
                Powered by{" "}
                <span style={{ color: config.color }}>{`${config.name} `}</span>
              </div>
            )} */}
          </div>
        </>
      )}

      <AnimatePresence>
        {showDeleteConfirm && <DeleteConfirmDialog />}
      </AnimatePresence>
    </div>
  );
};

export default ChatHistory;
